import { Component, Input, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { lastValueFrom, map } from 'rxjs';
import {
  GiftCycleControllerService,
  GiftCycleItemRequest,
  GiftCycleRequest,
  GiftCycleResponseDTO,
  ProductInfoDTO
} from 'src/app/admin-api';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

interface ProductRowState {
  loading: boolean;
  removeDisabled: boolean;
  error?: string;
  productName?: string;
  imageUrl?: string;
  searchedSku?: number;
  searchingSku?: number;
}

interface MarcoSlotState {
  marcoValue: number;
  label: string;
  products: ProductRowState[];
}

interface SearchedProduct extends ProductInfoDTO {
  productVariantName?: string;
  defaultImageURL?: string;
}

const MARCO_DEFINITIONS: { value: number; label: string }[] = [
  { value: 1, label: '1ª indicação' },
  { value: 2, label: '2ª indicação' },
  { value: 3, label: '3ª indicação' },
  { value: 4, label: '4ª indicação' },
  { value: 5, label: '5ª indicação' },
  { value: 10, label: '10ª indicação' },
  { value: 15, label: '15ª indicação' },
  { value: 20, label: '20ª indicação' },
  { value: 1000, label: '1ª indicação vida' }
];

@Component({
  selector: 'app-mgm-cycle-form',
  templateUrl: './mgm-cycle-form.component.html',
  styleUrl: './mgm-cycle-form.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class MgmCycleFormComponent implements OnInit {
  private giftCycleService = inject(GiftCycleControllerService);
  private title = inject(Title);
  private router = inject(Router);
  private messageService = inject(MessageService);

  @Input() cycleId?: string;

  isEditing = false;
  canSubmit = false;
  submitLabel = 'Criar Ciclo';

  form = new FormGroup({
    referenceMonth: new FormControl<Date>(null, Validators.required),
    slots: new FormArray(
      MARCO_DEFINITIONS.map(() =>
        new FormGroup({
          products: new FormArray([this.createProductFormGroup()])
        })
      )
    )
  });

  slotStates: MarcoSlotState[] = MARCO_DEFINITIONS.map((m) => ({
    marcoValue: m.value,
    label: m.label,
    products: [this.createProductState()]
  }));

  get slotsArray(): FormArray {
    return this.form.controls['slots'] as FormArray;
  }

  slotProducts(si: number): FormArray {
    return (this.slotsArray.at(si) as FormGroup).controls['products'] as FormArray;
  }

  getSkuControl(si: number, pi: number): FormControl<number> {
    return (this.slotProducts(si).at(pi) as FormGroup).controls['sku'] as FormControl<number>;
  }

  invalidSku(si: number, pi: number): boolean {
    const control = this.getSkuControl(si, pi);
    return control.invalid && (control.dirty || control.touched);
  }

  invalidControl(control: string): boolean {
    return (
      this.form.controls[control].invalid &&
      (this.form.controls[control].dirty || this.form.controls[control].touched)
    );
  }

  async ngOnInit(): Promise<void> {
    if (this.cycleId) {
      this.isEditing = true;
      this.submitLabel = 'Salvar Alterações';
      this.title.setTitle('Marketing - Editar Ciclo');
      await this.loadCycle(Number(this.cycleId));
    } else {
      this.title.setTitle('Marketing - Novo Ciclo');
      this.form.controls['referenceMonth'].setValue(this.suggestNextReferenceMonth());
      this.updateCanSubmit();
    }
  }

  async loadCycle(id: number): Promise<void> {
    LoaderService.showLoader();
    try {
      const cycle: GiftCycleResponseDTO = await lastValueFrom(
        this.giftCycleService.getCycle(id).pipe(map((r) => r.result))
      );
      this.form.controls['referenceMonth'].setValue(
        this.parseReferenceMonth(cycle.referenceMonth)
      );

      const itemsByMarco = new Map<number, GiftCycleResponseDTO['gifts'][number]>();
      (cycle.gifts || []).forEach((g) => {
        if (g.marcoValue != null) itemsByMarco.set(g.marcoValue, g);
      });

      this.slotStates.forEach((slotState, si) => {
        const item = itemsByMarco.get(slotState.marcoValue);
        if (!item || !item.products?.length) return;

        const productCount = item.products.length;

        while (this.slotProducts(si).length < productCount) {
          this.slotProducts(si).push(this.createProductFormGroup());
        }
        while (this.slotProducts(si).length > productCount) {
          this.slotProducts(si).removeAt(this.slotProducts(si).length - 1);
        }

        slotState.products = item.products.map((p, pi) => {
          this.getSkuControl(si, pi).setValue(p.milleniumSku ?? null);
          return {
            loading: false,
            removeDisabled: false,
            searchedSku: p.milleniumSku,
            productName: p.productName,
            imageUrl: p.imageUrl
          };
        });
      });
      this.rebuildRemoveDisabled();
      this.updateCanSubmit();
    } catch (error) {
      AppDialogService.showErrorDialog(error, true);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  addProduct(si: number): void {
    this.slotStates[si].products.push(this.createProductState());
    const pi = this.slotProducts(si).length;
    this.slotProducts(si).push(this.createProductFormGroup());
    this.updateRemoveDisabled(si, pi);
    this.updateCanSubmit();
  }

  removeProduct(si: number, pi: number): void {
    if (this.slotStates[si].products.length <= 1) {
      this.clearProduct(si, pi);
      return;
    }
    this.slotStates[si].products.splice(pi, 1);
    this.slotProducts(si).removeAt(pi);
    this.rebuildRemoveDisabled();
    this.updateCanSubmit();
  }

  onSkuInput(si: number, pi: number): void {
    const state = this.slotStates[si].products[pi];
    const sku = this.getSkuControl(si, pi).value;
    if (state.searchedSku === sku) {
      this.updateCanSubmit();
      this.updateRemoveDisabled(si, pi);
      return;
    }

    state.error = undefined;
    state.productName = undefined;
    state.imageUrl = undefined;
    state.searchedSku = undefined;
    state.searchingSku = undefined;
    state.loading = false;
    this.updateCanSubmit();
    this.updateRemoveDisabled(si, pi);
  }

  async onSkuBlur(si: number, pi: number): Promise<void> {
    const state = this.slotStates[si].products[pi];
    const sku = this.getSkuControl(si, pi).value;
    if (!sku) {
      state.error = undefined;
      state.productName = undefined;
      state.imageUrl = undefined;
      state.searchedSku = undefined;
      state.searchingSku = undefined;
      state.loading = false;
      this.updateCanSubmit();
      this.updateRemoveDisabled(si, pi);
      return;
    }
    if (state.searchedSku === sku && state.productName) return;

    await this.searchSku(si, pi);
  }

  updateCanSubmit(): void {
    this.canSubmit =
      !!this.form.controls['referenceMonth'].value &&
      this.slotStates.every((slot, si) =>
        slot.products.length > 0 &&
        slot.products.every((p, pi) => !!this.getSkuControl(si, pi).value && !!p.productName)
      );
  }

  async submit(): Promise<void> {
    if (!this.canSubmit) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail:
          'Preencha o mês de referência e busque um produto válido para cada SKU dos 9 marcos.'
      });
      return;
    }

    if (this.hasDuplicateSkus()) return;

    const referenceMonth = this.dateToReferenceMonth(
      this.form.controls['referenceMonth'].value
    );

    const body: GiftCycleRequest = {
      referenceMonth,
      gifts: this.slotStates.map<GiftCycleItemRequest>((slot, si) => ({
        marcoValue: slot.marcoValue,
        milleniumSkus: slot.products.map((_, pi) => this.getSkuControl(si, pi).value)
      }))
    };

    LoaderService.showLoader();
    try {
      const saved = this.isEditing
        ? await lastValueFrom(
            this.giftCycleService
              .updateCycle(body, Number(this.cycleId))
              .pipe(map((r) => r.result))
          )
        : await lastValueFrom(
            this.giftCycleService
              .createCycle(body)
              .pipe(map((r) => r.result))
          );
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: this.isEditing ? 'Ciclo atualizado.' : 'Ciclo criado.'
      });
      this.router.navigate(['/marketing/mgm', saved.giftCycleId]);
    } catch (error) {
      AppDialogService.showErrorDialog(error, true);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  cancel(): void {
    if (this.isEditing && this.cycleId) {
      this.router.navigate(['/marketing/mgm', this.cycleId]);
    } else {
      this.router.navigate(['/marketing/mgm']);
    }
  }

  private async searchSku(si: number, pi: number): Promise<void> {
    const state = this.slotStates[si].products[pi];
    const sku = this.getSkuControl(si, pi).value;
    if (!sku) {
      state.error = 'Informe o SKU';
      this.updateRemoveDisabled(si, pi);
      return;
    }
    state.loading = true;
    state.searchingSku = sku;
    state.error = undefined;
    state.productName = undefined;
    state.imageUrl = undefined;
    this.updateRemoveDisabled(si, pi);
    try {
      const product = (await lastValueFrom(
        this.giftCycleService.searchProduct(sku).pipe(map((r) => r.result))
      )) as SearchedProduct | null;
      if (state.searchingSku !== sku || this.getSkuControl(si, pi).value !== sku) return;
      if (!product) {
        state.error = 'Produto não encontrado';
        this.updateRemoveDisabled(si, pi);
        return;
      }
      state.searchedSku = sku;
      state.productName =
        product.productVariantName ||
        product.productName ||
        'Produto sem nome';
      state.imageUrl = product.defaultImageURL || product.imageUrl;
    } catch (error) {
      if (state.searchingSku !== sku || this.getSkuControl(si, pi).value !== sku) return;
      const err = error as { error?: { message?: string }; message?: string };
      state.error = err?.error?.message || err?.message || 'Falha ao buscar SKU';
    } finally {
      if (state.searchingSku === sku) {
        state.loading = false;
        state.searchingSku = undefined;
        this.updateCanSubmit();
        this.updateRemoveDisabled(si, pi);
      }
    }
  }

  private clearProduct(si: number, pi: number): void {
    const state = this.slotStates[si].products[pi];
    state.error = undefined;
    state.productName = undefined;
    state.imageUrl = undefined;
    state.searchedSku = undefined;
    state.searchingSku = undefined;
    state.loading = false;
    this.getSkuControl(si, pi).setValue(null);
    this.getSkuControl(si, pi).markAsPristine();
    this.getSkuControl(si, pi).markAsUntouched();
    this.updateCanSubmit();
    this.updateRemoveDisabled(si, pi);
  }

  private rebuildRemoveDisabled(): void {
    for (let si = 0; si < this.slotStates.length; si++) {
      const slot = this.slotStates[si];
      for (let pi = 0; pi < slot.products.length; pi++) {
        this.updateRemoveDisabled(si, pi);
      }
    }
  }

  private updateRemoveDisabled(si: number, pi: number): void {
    const slot = this.slotStates[si];
    const state = slot.products[pi];
    if (slot.products.length > 1) {
      state.removeDisabled = false;
      return;
    }
    const canRemove =
      !!this.getSkuControl(si, pi).value ||
      !!state.productName ||
      !!state.imageUrl ||
      !!state.error ||
      state.loading;
    state.removeDisabled = !canRemove;
  }

  private hasDuplicateSkus(): boolean {
    const seen = new Set<number>();
    for (let si = 0; si < this.slotStates.length; si++) {
      const slot = this.slotStates[si];
      const localSeen = new Set<number>();
      for (let pi = 0; pi < slot.products.length; pi++) {
        const sku = this.getSkuControl(si, pi).value;
        if (sku == null) continue;
        if (localSeen.has(sku)) {
          this.messageService.add({
            severity: 'warn',
            summary: 'SKU duplicada',
            detail: `O SKU ${sku} aparece mais de uma vez no marco "${slot.label}".`
          });
          return true;
        }
        if (seen.has(sku)) {
          this.messageService.add({
            severity: 'warn',
            summary: 'SKU duplicada',
            detail: `O SKU ${sku} já está em outro marco do mesmo ciclo.`
          });
          return true;
        }
        localSeen.add(sku);
        seen.add(sku);
      }
    }
    return false;
  }

  private createProductFormGroup(): FormGroup {
    return new FormGroup({
      sku: new FormControl<number>(null, Validators.required)
    });
  }

  private createProductState(): ProductRowState {
    return {
      loading: false,
      removeDisabled: true
    };
  }

  private dateToReferenceMonth(date: Date): number {
    return date.getFullYear() * 100 + (date.getMonth() + 1);
  }

  private parseReferenceMonth(ref?: number): Date | null {
    if (!ref) return null;
    const s = String(ref);
    if (s.length !== 6) return null;
    return new Date(Number(s.substring(0, 4)), Number(s.substring(4, 6)) - 1, 1);
  }

  private suggestNextReferenceMonth(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 1);
  }
}
